import type { RefundQuoteData, RefundQuoteTicket, TicketTax } from './types'

/**
 * Whether a carrier gives back its YQ/YR surcharges when a refundable fare is refunded, as the Ops App
 * table records it (SPEC.md §3, the YQ/YR floor). A carrier in the table carries no YQ/YR uncertainty;
 * one that isn't keeps the estimation floor and its "if the carrier refunds surcharges" range.
 */
export type CarrierSurchargePolicy = 'refunds' | 'withholds'

/** Two-letter carrier code → policy. */
export type CarrierSurchargeTable = Record<string, CarrierSurchargePolicy>

const SURCHARGE_CODES = new Set(['YQ', 'YR'])
const TOLERANCE = 1

/** The plating carrier from the ticket check, else the first fare component's airline. */
export function surchargeCarrierOf(rqd: RefundQuoteData): string | undefined {
  return (rqd.carrier ?? rqd.fareRules?.[0]?.airline)?.toUpperCase()
}

export function surchargePolicyFor(
  table: CarrierSurchargeTable | undefined,
  rqd: RefundQuoteData,
): CarrierSurchargePolicy | undefined {
  const carrier = surchargeCarrierOf(rqd)
  return carrier ? table?.[carrier] : undefined
}

/**
 * What an automated Sabre refund quote says this carrier did with a ticket's YQ/YR: the refunded taxes
 * equal every tax paid (`refunded`), or every tax but YQ/YR (`withheld`). Null when the ticket carries
 * no evidence (no automated quote, or no YQ/YR on it).
 */
export function observeSurchargeRefund(
  ticket: Pick<RefundQuoteTicket, 'refundTotals'>,
  taxes: TicketTax[] | undefined,
): 'refunded' | 'withheld' | 'unclear' | null {
  if (!ticket.refundTotals || !taxes?.length) return null

  const surcharges = taxes.reduce((sum, t) => sum + (SURCHARGE_CODES.has(t.taxCode) ? parseFloat(t.amount) || 0 : 0), 0)
  if (surcharges <= 0) return null

  const paid = taxes.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0)
  const refunded = parseFloat(ticket.refundTotals.taxes) || 0
  if (Math.abs(refunded - paid) <= TOLERANCE) return 'refunded'
  if (Math.abs(refunded - (paid - surcharges)) <= TOLERANCE) return 'withheld'
  return 'unclear'
}
